import { useSelector } from 'react-redux';
import { Button, colors, Txt } from '@vst/beam';
import { useTranslation } from 'react-i18next';

import {
  AEMContentProvider,
  AemFeature,
  createAemUrl,
  SKIP_PARAMETER_CONSTANT,
} from '@app/components/AdobeExperienceFragments';
import { selectBroadbandLabelAttributes, selectLocale, selectUserInfo } from '@app/redux';
import { selectConfig } from '@app/redux/config';
import { selectCurrentPlanPrice, selectCurrentUsage, selectPlanCharacteristics } from '@app/redux/plan';
import { getCurrencySymbol, isVoiceOnlyPlan, showTitleAddons } from '@app/utils';

import Loading from '../Loading';
import MarketingAttributes from '../MarketingAttributes';
import PlanCard from '../PlanCard';
import PlanCardBody from '../PlanCardBody';
import PlanCardPrice from '../PlanCardPrice';
import TitleAddOns from '../TitleAddOns';
import { Variant } from '../../types';

type CurrentPlanProps = {
  isCollapsibleCurrentPlan?: boolean;
};

const CurrentPlan = ({ isCollapsibleCurrentPlan }: CurrentPlanProps) => {
  const { t } = useTranslation('ChangePlan');

  const {
    locale: { userLocale },
  } = useSelector(selectLocale);

  const {
    data: { upi, serviceAreaId, serviceId, satelliteName, iduDeviceModel },
  } = useSelector(selectBroadbandLabelAttributes);

  const { price, errors: priceError, loading: priceLoading } = useSelector(selectCurrentPlanPrice);

  const {
    characteristics: {
      attributes,
      id,
      name,
      dataCap,
      titleAddOns,
      usageMeter,
      productFamily,
      inflectionPointText,
      downloadSpeed,
      downloadUnits,
      fiberSpeedText,
    },
    loading: characteristicsLoading,
  } = useSelector(selectPlanCharacteristics);
  const isHibernation = name.toLowerCase().includes('hibernation');

  const loading = characteristicsLoading || priceLoading;

  const {
    data: { monthlyDataCap },
  } = useSelector(selectCurrentUsage);

  const { aemBaseUrl } = useSelector(selectConfig);
  const { loading: broadbandLabelAttributesLoading } = useSelector(selectBroadbandLabelAttributes);

  const {
    userInfo: { accountType },
  } = useSelector(selectUserInfo);

  /*
  This is an extra guardrail in place,
  normally for Unleashed current plans no other plans are available for transition
  and the cards are not displayed
  */
  const showTitleAddOns = showTitleAddons(productFamily, name, titleAddOns);

  const loadingCards = [{ name, variant: Variant.BLUE }];

  const hasVoiceOnlyPlan = isVoiceOnlyPlan(id);

  const showMarketingAttributes = Array.isArray(attributes) && attributes.length;

  return loading ? (
    <Loading cards={loadingCards} />
  ) : (
    <PlanCard
      variant={Variant.BLUE}
      name={name}
      broadbandLabel={
        !broadbandLabelAttributesLoading && (
          <AEMContentProvider
            src={createAemUrl({
              baseUrl: aemBaseUrl,
              locale: userLocale,
              feature: AemFeature.PLAN_LABEL,
              accountType,
              additionalSelectors: ['pidi'],
              suffixes: {
                upi,
                serviceAreaId,
                offerId: SKIP_PARAMETER_CONSTANT,
                satelliteName,
                serviceId,
                iduDeviceModel,
              },
            })}
            id={'current-plan-label'}
            style={{ textAlign: 'left' }}
          />
        )
      }
    >
      {isHibernation ? (
        <Txt variant="heading4" style={{ margin: '0 auto', color: colors['blue'][700] }}>
          {name}
        </Txt>
      ) : (
        !hasVoiceOnlyPlan && (
          <PlanCardBody
            id={id}
            characteristics={{
              dataCap: dataCap ?? monthlyDataCap.toString(),
              downloadSpeed,
              downloadUnits,
              usageMeter,
              productFamily,
              offerName: name,
              attributes,
              fiberSpeedText,
            }}
            disclosure={inflectionPointText}
            isCollapsibleCurrentPlan={isCollapsibleCurrentPlan}
          />
        )
      )}
      {showTitleAddOns && <TitleAddOns characteristic={titleAddOns} />}

      <Button disabled={true} fluid={true} variant="primary" buttonSize="large">
        {t('currentPlan.button')}
      </Button>

      {!priceError && <PlanCardPrice price={price} currency={getCurrencySymbol(userLocale)} />}

      {showMarketingAttributes ? <MarketingAttributes attributes={attributes} /> : null}
    </PlanCard>
  );
};

export default CurrentPlan;
